/**
 * Notification category taxonomy — the SINGLE source of truth mapping a raw
 * notification `category` (as emitted by feature code: `checkup`, `care`,
 * `billing`, `vetting`, `system`, …) to the bucket that governs its delivery.
 *
 * Both the delivery enforcement (platform-api send paths) and the settings UI
 * import this so a notification's category and the toggle that governs it can
 * never drift apart. Adding a new emitter category = add one line here.
 *
 * ─── Two kinds of bucket, and why the distinction is load-bearing ─────
 *
 * `NotificationBucket` is the set of buckets a PERSON CONTROLS — the four
 * toggles in Settings, and the four values a `List-Unsubscribe` link may name.
 *
 * `transactional` is the EXEMPT bucket: mail a recipient cannot switch off,
 * because switching it off would remove the only channel by which they can
 * recover an account or be told their access changed. It is deliberately NOT a
 * member of `NotificationBucket` — there is no toggle for it, the unsubscribe
 * route refuses it, and nothing renders it as a preference row.
 *
 * Until W4.4 there was no exempt bucket at all: `account` resolved to `system`,
 * a bucket the user can switch off, so a password-reset or security mail was
 * SUPPRESSIBLE. That is the hole this file closes.
 *
 * ─── The fallback stays SUPPRESSIBLE, on purpose (DW4-3) ──────────────
 *
 * An unknown or absent category resolves to `system` — a real, user-controlled
 * bucket. A sender that forgets to register its category must fail CLOSED
 * (honours the recipient's System toggle) and must never fail OPEN into the
 * exempt bucket, which would let any new emitter silently declare itself
 * un-unsubscribable.
 */

/** The four buckets a person controls (`settings.notifications.categories`). */
export type NotificationBucket = 'hero' | 'system' | 'social' | 'alert';

export const NOTIFICATION_BUCKETS: readonly NotificationBucket[] = [
  'hero',
  'system',
  'social',
  'alert',
] as const;

/**
 * The exempt bucket. Not user-controllable, never offered by the unsubscribe
 * route, never suppressed by an address-level opt-out.
 *
 * Reserve it for mail whose absence breaks the recipient's ability to hold or
 * recover their account: credential resets, security notices, account-state
 * changes. Marketing, reminders and digests are never transactional — if in
 * doubt, leave the category out of this bucket and it stays suppressible.
 */
export const TRANSACTIONAL_BUCKET = 'transactional' as const;

/** Every bucket a category can resolve to — controllable ones plus the exempt one. */
export type NotificationDeliveryBucket = NotificationBucket | typeof TRANSACTIONAL_BUCKET;

/**
 * Raw emitter category → delivery bucket. Lowercased keys.
 *  • hero          — journey / hero progress the citizen opted into (CheckUp, Care, …)
 *  • system        — platform housekeeping the recipient may switch off
 *  • social        — people & company interactions (team, vetting, invitations, messages)
 *  • alert         — action-required, time- or money-sensitive (billing, payment, overdue)
 *  • transactional — EXEMPT. See `TRANSACTIONAL_BUCKET`.
 */
const CATEGORY_TO_BUCKET: Readonly<Record<string, NotificationDeliveryBucket>> = {
  // hero / journey progress
  checkup: 'hero',
  care: 'hero',
  qultureup: 'hero',
  teleportal: 'hero',
  passport: 'hero',
  mobility: 'hero',
  evidence: 'hero',
  // system / platform housekeeping the recipient may switch off
  system: 'system',
  integration: 'system',
  /**
   * EXEMPT. `account` is credential and account-state mail — a password reset,
   * a security notice, a change to how someone signs in.
   *
   * It used to resolve to `system`, which meant the opt-out page could switch
   * off the only channel that can restore access, while that same page promised
   * in so many words that "essential account emails — password resets and
   * security notices — will still reach you". The promise was copy; this line
   * is what makes it true.
   */
  account: TRANSACTIONAL_BUCKET,
  /**
   * EXEMPT. A certified invoice-receipt is the buyer's legal document for a
   * payment (docs/BILLING-CERTIFIED-INVOICING-JASMIN.md §4.4): the notice that
   * it is issued is not marketing and not an alert a person mutes — like a
   * receipt at a till, it is handed over. The document also stays under
   * Billing in the portal.
   */
  fiscal_document: TRANSACTIONAL_BUCKET,
  /**
   * EXEMPT. What the platform sends a partner agency's case inbox about a
   * person's case — a contact request, the dossier — at the person's own
   * request and with their consent. The inbox is the agency's, no account's:
   * nobody's notification settings govern it, and an opt-out link would be a
   * button that withholds what the person asked for.
   */
  inout_agency: TRANSACTIONAL_BUCKET,
  // social / people & company
  social: 'social',
  team: 'social',
  vetting: 'social',
  invitation: 'social',
  message: 'social',
  /**
   * `general` is the `message_templates` category DEFAULT (migration 0023) and
   * the category of three live system templates — `coaching.message`,
   * `cohort.welcome` and `program.enrollment`. It was UNMAPPED, so all three
   * landed in `system` by accident rather than by decision.
   *
   * Harmless while nothing acted on the bucket; not harmless once an
   * address-level opt-out does. All three are a company communicating with a
   * person it works with — a coach's note, a cohort welcome, an enrolment
   * confirmation — which is `social`, the same bucket `message` and `team`
   * already use. Registering it here is what stops the System toggle (the one
   * whose label reads "Account & system") governing a coaching message.
   */
  general: 'social',
  /**
   * A sponsorship offer is a people-and-companies interaction, and it was
   * MISSING here — so it fell through to the `system` fallback.
   *
   * Harmless while nothing acted on the bucket; not harmless once
   * `List-Unsubscribe` does. The one-click opt-out flips the bucket the
   * template resolves to, so a sponsorship reminder's Unsubscribe button would
   * have switched off `system` — and the opt-out page promises in so many words
   * that essential account mail still arrives. Classifying it correctly is what
   * makes that promise true.
   */
  sponsorship: 'social',
  // alert / action-required
  alert: 'alert',
  /**
   * InOut's legal notices — a residence limit getting close, a presence
   * question, a renewal window open. Action-required, and the person's to
   * govern: `alert`, never exempt. InOut's own notice preferences choose the
   * channels on top; the engine never lets another nudge or the email quota
   * silence one (AC-23).
   */
  inout: 'alert',
  billing: 'alert',
  payment: 'alert',
  overdue: 'alert',
};

/**
 * Resolve a raw category to the bucket that governs DELIVERY, including the
 * exempt one.
 *
 * This is the function every send path must use. An unknown or absent category
 * maps to `system` — a REAL, user-suppressible bucket (never silently
 * un-gated), so a new emitter that forgets to register here still honours a
 * user's System toggle rather than bypassing preferences entirely.
 */
export function resolveDeliveryBucket(category?: string | null): NotificationDeliveryBucket {
  if (!category) return 'system';
  return CATEGORY_TO_BUCKET[category.toLowerCase()] ?? 'system';
}

/**
 * Can a recipient switch this bucket off?
 *
 * A type guard, so the caller that answers "yes" is handed a
 * `NotificationBucket` and can hand it straight to a toggle or an unsubscribe
 * link — there is no path that names the exempt bucket in either place.
 */
export function isSuppressibleBucket(
  bucket: NotificationDeliveryBucket
): bucket is NotificationBucket {
  return bucket !== TRANSACTIONAL_BUCKET;
}

/**
 * Resolve a raw category to one of the four buckets a person controls.
 *
 * For DISPLAY only — the notification bell's icon, a feed grouping. Exempt mail
 * has no toggle of its own, so it is shown under `system`, which is where a
 * reader would look for it.
 *
 * Never use this to decide whether to send: it cannot express "exempt", so it
 * would hand transactional mail back a suppressible bucket, which is exactly the
 * defect `resolveDeliveryBucket` exists to fix. Delivery paths call
 * `resolveDeliveryBucket` + `isSuppressibleBucket`.
 */
export function resolveNotificationBucket(category?: string | null): NotificationBucket {
  const bucket = resolveDeliveryBucket(category);
  return isSuppressibleBucket(bucket) ? bucket : 'system';
}

/**
 * Message-template categories that are the PLATFORM's own mail, sent by the
 * platform at the moment it applies: `account` (codes and security notices),
 * `fiscal_document` (a certified invoice is issued) and `inout_agency` (to a
 * partner agency's case inbox, at the person's request), all also EXEMPT from
 * every opt-out (`CATEGORY_TO_BUCKET` → transactional), `inout` (the
 * person's legal notices, delivered in the `alert` bucket the person governs)
 * and `qultureup` (an External World's notices to the learner, in the `hero`
 * bucket).
 * An admin may read and reword such a template, but never create one
 * in these categories, move one in or out, duplicate it, switch it off or send
 * it from the message centre — exempt mail with arbitrary copy would be a way
 * round every opt-out. The API (`BusinessService`) and the business portal
 * read this one list.
 */
export const PLATFORM_ONLY_TEMPLATE_CATEGORIES = [
  'account',
  'fiscal_document',
  'inout',
  'inout_agency',
  'qultureup',
] as const;
export type PlatformOnlyTemplateCategory = (typeof PLATFORM_ONLY_TEMPLATE_CATEGORIES)[number];

export function isPlatformOnlyTemplateCategory(
  category: string | null | undefined
): category is PlatformOnlyTemplateCategory {
  return (PLATFORM_ONLY_TEMPLATE_CATEGORIES as readonly string[]).includes(category ?? '');
}
